import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Paper, Stack, Grid, CircularProgress, IconButton,
  Chip, LinearProgress, Divider, Button,
} from "@mui/material";
import {
  Refresh as RefreshIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  AccessTime as ClockIcon,
  LocalFireDepartment as FireIcon,
  PersonOff as UnassignedIcon,
  NotificationsActive as FollowUpIcon,
  CheckCircle as CheckIcon,
} from "@mui/icons-material";
import {
  fetchDashboardStats, fetchDashboardCharts, reclassifyAllLeads,
  type DashboardStats, type DashboardCharts,
  STATUS_CONFIG, PIPELINE_ORDER,
} from "./leadApi";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";

const NAVY = "#0d1b2a";
const GOLD = "#c9a96e";

export default function LeadDashboard() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [charts, setCharts] = useState<DashboardCharts | null>(null);
  const [loading, setLoading] = useState(true);
  const [reclassifying, setReclassifying] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [s, c] = await Promise.all([
        fetchDashboardStats(),
        fetchDashboardCharts(),
      ]);
      setStats(s);
      setCharts(c);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress sx={{ color: GOLD }} />
      </Box>
    );
  }

  if (!stats || !charts) return null;

  const weekChange = stats.last_week > 0
    ? Math.round(((stats.this_week - stats.last_week) / stats.last_week) * 100)
    : stats.this_week > 0 ? 100 : 0;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
            Lead Dashboard
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            Analytics & insights across all leads
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            variant="outlined"
            startIcon={reclassifying ? <CircularProgress size={14} /> : <AutoFixHighOutlinedIcon />}
            disabled={reclassifying}
            onClick={async () => {
              setReclassifying(true);
              try { await reclassifyAllLeads(); loadData(); } catch {}
              setReclassifying(false);
            }}
            sx={{ textTransform: "none", fontSize: 12, borderColor: "#16a34a", color: "#16a34a", "&:hover": { bgcolor: "#f0fdf4" } }}
          >
            {reclassifying ? "Classifying..." : "AI Reclassify All"}
          </Button>
          <IconButton onClick={loadData}>
            <RefreshIcon />
          </IconButton>
        </Stack>
      </Stack>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Leads"
            value={stats.total}
            subtitle={`${stats.this_week} this week`}
            icon={<PeopleIcon />}
            color="#3b82f6"
            trend={weekChange}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Hot Leads"
            value={stats.hot_leads}
            subtitle="Need immediate attention"
            icon={<FireIcon />}
            color="#ef4444"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Conversion Rate"
            value={`${stats.conversion_rate}%`}
            subtitle={`${stats.by_status.confirmed} confirmed`}
            icon={<CheckIcon />}
            color="#16a34a"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Avg Response"
            value={formatMinutes(stats.avg_response_minutes)}
            subtitle="First response time"
            icon={<ClockIcon />}
            color="#8b5cf6"
          />
        </Grid>
      </Grid>

      {/* Alert Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #fef3c7",
              borderRadius: 2,
              bgcolor: "#fffbeb",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <FollowUpIcon sx={{ color: "#f59e0b", fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
                {stats.follow_up_required}
              </Typography>
              <Typography variant="body2" sx={{ color: "#92400e" }}>
                Leads needing follow-up
              </Typography>
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              border: "1px solid #fef2f2",
              borderRadius: 2,
              bgcolor: "#fff5f5",
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <UnassignedIcon sx={{ color: "#ef4444", fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>
                {stats.unassigned}
              </Typography>
              <Typography variant="body2" sx={{ color: "#991b1b" }}>
                Unassigned leads
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        {/* Conversion Funnel */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>
              Conversion Funnel
            </Typography>
            <Stack spacing={1.5}>
              {charts.funnel.map((f) => {
                const cfg = STATUS_CONFIG[f.status as keyof typeof STATUS_CONFIG];
                const maxCount = Math.max(...charts.funnel.map((x) => x.count), 1);
                const pct = (f.count / maxCount) * 100;
                return (
                  <Box key={f.status}>
                    <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                        {cfg?.label || f.status}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: cfg?.color }}>
                        {f.count}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={pct}
                      sx={{
                        height: 8,
                        borderRadius: 4,
                        bgcolor: "#f1f5f9",
                        "& .MuiLinearProgress-bar": {
                          bgcolor: cfg?.color || "#6b7280",
                          borderRadius: 4,
                        },
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid>

        {/* Event Type Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>
              Event Types
            </Typography>
            {charts.event_types.length > 0 ? (
              <Stack spacing={1.5}>
                {charts.event_types.map((et) => {
                  const maxCount = Math.max(...charts.event_types.map((x) => x.count), 1);
                  const pct = (et.count / maxCount) * 100;
                  return (
                    <Box key={et.event_type}>
                      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, color: NAVY, textTransform: "capitalize" }}
                        >
                          {et.event_type.replace(/_/g, " ")}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: GOLD }}>
                          {et.count}
                        </Typography>
                      </Stack>
                      <LinearProgress
                        variant="determinate"
                        value={pct}
                        sx={{
                          height: 8,
                          borderRadius: 4,
                          bgcolor: "#f1f5f9",
                          "& .MuiLinearProgress-bar": { bgcolor: GOLD, borderRadius: 4 },
                        }}
                      />
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography variant="body2" sx={{ color: "#94a3b8", textAlign: "center", py: 4 }}>
                No data yet
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Source Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>
              Lead Sources
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {charts.sources.map((s) => (
                <Chip
                  key={s.source}
                  label={`${s.source}: ${s.count}`}
                  sx={{
                    bgcolor: sourceColor(s.source),
                    color: "#fff",
                    fontWeight: 600,
                    textTransform: "capitalize",
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* AI Score Distribution */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>
              AI Lead Scoring
            </Typography>
            <Stack direction="row" spacing={2}>
              {charts.ai_scores.map((a) => (
                <Paper
                  key={a.ai_score}
                  elevation={0}
                  sx={{
                    flex: 1,
                    p: 2,
                    textAlign: "center",
                    border: "1px solid #e5e7eb",
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="h4" sx={{ fontWeight: 700, color: aiColor(a.ai_score) }}>
                    {a.count}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "#64748b", textTransform: "capitalize", fontWeight: 600 }}
                  >
                    {a.ai_score === "hot" ? "🔥" : a.ai_score === "warm" ? "☀️" : "❄️"}{" "}
                    {a.ai_score}
                  </Typography>
                </Paper>
              ))}
            </Stack>
          </Paper>
        </Grid>

        {/* Status Breakdown */}
        <Grid item xs={12}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid #e5e7eb", borderRadius: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: NAVY, mb: 2 }}>
              Status Breakdown
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {PIPELINE_ORDER.map((s) => {
                const cfg = STATUS_CONFIG[s];
                const count = stats.by_status[s] || 0;
                return (
                  <Paper
                    key={s}
                    elevation={0}
                    sx={{
                      px: 2.5,
                      py: 1.5,
                      border: `1px solid ${cfg.color}30`,
                      borderRadius: 2,
                      bgcolor: cfg.bgColor,
                      textAlign: "center",
                      minWidth: 120,
                    }}
                  >
                    <Typography variant="h5" sx={{ fontWeight: 700, color: cfg.color }}>
                      {count}
                    </Typography>
                    <Typography variant="caption" sx={{ color: cfg.color, fontWeight: 600 }}>
                      {cfg.label}
                    </Typography>
                  </Paper>
                );
              })}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

/* ── Helper Components ────────────────────────────────────────────────── */

function StatCard({
  title, value, subtitle, icon, color, trend,
}: {
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  color: string;
  trend?: number;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: "1px solid #e5e7eb",
        borderRadius: 2,
        display: "flex",
        gap: 2,
        alignItems: "center",
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2,
          bgcolor: `${color}15`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: color,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
          {title}
        </Typography>
        <Stack direction="row" alignItems="baseline" spacing={1}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#0d1b2a" }}>
            {value}
          </Typography>
          {trend !== undefined && (
            <Chip
              size="small"
              icon={trend >= 0 ? <TrendingUpIcon sx={{ fontSize: "14px !important" }} /> : <TrendingDownIcon sx={{ fontSize: "14px !important" }} />}
              label={`${trend >= 0 ? "+" : ""}${trend}%`}
              sx={{
                height: 20,
                fontSize: "0.65rem",
                fontWeight: 600,
                bgcolor: trend >= 0 ? "#dcfce7" : "#fef2f2",
                color: trend >= 0 ? "#16a34a" : "#ef4444",
                "& .MuiChip-icon": { color: "inherit" },
              }}
            />
          )}
        </Stack>
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          {subtitle}
        </Typography>
      </Box>
    </Paper>
  );
}

function formatMinutes(min: number): string {
  if (min < 60) return `${min}m`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

function sourceColor(source: string): string {
  switch (source) {
    case "website": return "#3b82f6";
    case "email": return "#8b5cf6";
    case "phone": return "#14b8a6";
    case "walk_in": return "#f59e0b";
    default: return "#6b7280";
  }
}

function aiColor(score: string): string {
  switch (score) {
    case "hot": return "#ef4444";
    case "warm": return "#f59e0b";
    case "cold": return "#3b82f6";
    default: return "#6b7280";
  }
}
